import { Table } from 'antd';
import { useEffect, useState } from 'react';
import { generateRandomKey } from '@/utils/random';
import { getUsersByIds } from '@/services/api/coreService';
import { TableViewProps } from '@/types/views';
import { useTranslation } from 'react-i18next';

const TableView = ({
  tableData,
  currentNode,
  setCurrentNode,
  usersInDocuments,
  setUsersInDocuments,
  columns,
  onExpand,
  button = <></>,
}: TableViewProps) => {
  const { t } = useTranslation();
  const [paths, setPaths] = useState<any[]>([]);
  const [displayData, setDisplayData] = useState<any>(tableData);

  useEffect(() => {
    const loadParent = (node: any): any[] => {
      if (!node || node === null) {
        return [];
      }
      const isFile = node.isLeaf === true || (node.type && node.type !== '');
      if (isFile) {
        return [];
      }
      const path: any = !node?.name ? null : (
        <a key={node?.key} onClick={() => backToNodeX(node)}>
          {node?.name === 'root' ? t('home.documents.home') : node?.name}
        </a>
      );
      if (node?.parent) {
        return [...loadParent(node?.parent), path];
      }
      return path ? [path] : [];
    };
    const isAtRoot = !currentNode || currentNode === null;
    setPaths([
      <a key={generateRandomKey()} onClick={() => backToNodeX(null)}>
        {t('home.documents.home')}
      </a>,
      ...loadParent(currentNode),
    ]);
    getUsersInDocument();
  }, [tableData, currentNode, t]);

  const backToNodeX = (node: any) => {
    setCurrentNode(node);
    onExpand(node, true);
  };

  const getUsersInDocument = async () => {
    const user_ids: any = [];
    tableData?.forEach((record: any) => {
      if (
        record?.created_by &&
        !record?.default &&
        (!usersInDocuments || !usersInDocuments[record?.created_by])
      )
        user_ids.push(record?.created_by);
      if (
        record?.updated_by &&
        (!usersInDocuments || !usersInDocuments[record?.updated_by])
      )
        user_ids.push(record?.updated_by);
    });
    if (user_ids.length > 0) {
      try {
        const response = await getUsersByIds({
          user_ids: Array.from(new Set(user_ids)),
        });
        const usersArray = Array.isArray(response)
          ? response
          : response?.data && Array.isArray(response.data)
            ? response.data
            : [];

        const newFoundUsers = usersArray.reduce((users: any, obj: any) => {
          if (obj && obj.id) {
            users[obj.id] = obj;
          }
          return users;
        }, {});
        const listNewUsers = { ...usersInDocuments, ...newFoundUsers };
        setUsersInDocuments(listNewUsers);
        setDisplayData(() =>
          tableData?.map((record: any) => ({
            ...record,
            created_by_name:
              listNewUsers[record.created_by]?.name || record.created_by,
            updated_by:
              listNewUsers[record.updated_by]?.name || record.updated_by,
          })),
        );
      } catch (error) {
        setDisplayData(() =>
          tableData?.map((record: any) => ({
            ...record,
            created_by_name:
              usersInDocuments[record.created_by]?.name || record.created_by,
            updated_by:
              usersInDocuments[record.updated_by]?.name || record.updated_by,
          })),
        );
      }
    } else {
      setDisplayData(() =>
        tableData?.map((record: any) => ({
          ...record,
          created_by_name:
            usersInDocuments[record.created_by]?.name || record.created_by,
          updated_by:
            usersInDocuments[record.updated_by]?.name || record.updated_by,
        })),
      );
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: '10px',
        }}
      >
        <span>
          {paths
            .filter(path => path != null)
            .map((path, index) => {
              return (
                <span key={generateRandomKey()}>
                  {index > 0 && ' / '}
                  <b>{path}</b>
                </span>
              );
            })}
        </span>
        {button}
      </div>
      <Table
        columns={columns}
        dataSource={displayData}
        rowKey={record => {
          if (record.key) {
            return record.key;
          }
          if (record.id) {
            return record.id;
          }
          return `${record.name || 'row'}-${record.path || ''}-${record.created_at || ''}`;
        }}
        expandable={{
          expandIcon: () => null,
        }}
      />
    </div>
  );
};

export default TableView;
